import type { FormDefinition } from './types';

/*
 * Submit-time kind check for the vendor form's answers. RES-1334.
 *
 * The forms-admin renderer is controlled — we hand it `values` back — so it normalises our copy
 * before validating it. `"a"` in a number field becomes `null`, the renderer validates THAT, calls
 * the form valid, and we post the original. forms-service rejects it minutes later in a consumer
 * the tenant cannot see, long after the booking is confirmed.
 *
 * Rules mirror `@hqo/forms-response-validator@0.0.20`, the package forms-service and the renderer
 * both already run. Mirrored by hand only because this app cannot yet depend on it; the follow-up
 * is to import it and delete all of this. Copied from source rather than the published tarball, so
 * a republish without a version bump would diverge silently.
 *
 * KIND ONLY. A min/max or option-membership violation is a value of the right kind, so the renderer
 * preserves it, its own zod catches it, and `isFormValid` already blocks it.
 *
 * FAILS OPEN everywhere: unwalkable definition, unmodelled type, absent answer, empty value. A
 * false positive stops a tenant who has already paid for the room from ordering anything at all.
 */

type AnswerValidator = (value: unknown) => boolean;

/*
 * Mirrors `isEmptyFieldValue` line for line, and must keep doing so — the wrapper short-circuits on
 * empty before the type schema, so flagging an empty value here would reject one forms-service
 * accepts. Note `false` (an unticked checkbox) and `NaN` count as empty.
 */
const isEmptyAnswer = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value === '';
  if (typeof value === 'number') return Number.isNaN(value);
  if (typeof value === 'boolean') return value === false;
  if (value instanceof Date) return Number.isNaN(value.getTime());
  if (Array.isArray(value)) return value.length === 0;

  return false;
};

/* `z.string()`. Covers phone too — forms-service has no phone builder and maps it to text. */
const isText: AnswerValidator = value => typeof value === 'string';

/*
 * `z.number()`, which rejects `'2'`. Accepting numeric strings would let the same failure through
 * under a different value; the renderer emits a parsed number for parseable input, so nothing
 * legitimate is caught.
 */
const isNumber: AnswerValidator = value => typeof value === 'number' && Number.isFinite(value);

/*
 * Looser than the schema's ISO union, on purpose. `normalizeDate` passes strings through verbatim,
 * so a malformed date string already reaches the renderer's zod; only non-strings are erased, and
 * those are what this catches.
 */
const isDate: AnswerValidator = value => {
  if (value instanceof Date) return !Number.isNaN(value.getTime());

  return typeof value === 'string' && !Number.isNaN(Date.parse(value));
};

/* The schema's own regex. `normalizeTime` nulls every wrong-format string, not just wrong kinds. */
const TIME_VALUE_REGEX = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;

const isTime: AnswerValidator = value => typeof value === 'string' && TIME_VALUE_REGEX.test(value);

/*
 * `z.array(z.string())` — Multiple choice and Multi-select are one type, and a single choice is
 * still an array. A bare string fails: `toStringArray` wraps one for the renderer, but we post ours.
 */
const isSelect: AnswerValidator = value => Array.isArray(value) && value.every(item => typeof item === 'string');

const isNonEmptyString = (value: unknown): boolean => typeof value === 'string' && value.length > 0;
const isPositiveInteger = (value: unknown): boolean =>
  typeof value === 'number' && Number.isInteger(value) && value > 0;

/* Element kinds checked here because `toFileInputArray` keeps any object carrying a `fileUuid`. */
const isFileEntry = (item: unknown): boolean => {
  if (typeof item !== 'object' || item === null) return false;

  const { fileUuid, fileName, fileSizeBytes } = item as Record<string, unknown>;

  return isNonEmptyString(fileUuid) && isNonEmptyString(fileName) && isPositiveInteger(fileSizeBytes);
};

const isFile: AnswerValidator = value => Array.isArray(value) && value.every(isFileEntry);

/* `z.boolean()`. Only `true` reaches it — `false` is empty above. */
const isCheckbox: AnswerValidator = value => typeof value === 'boolean';

/* A type absent from this map is not validated, so a new field type stays submittable. */
const ANSWER_VALIDATORS: Record<string, AnswerValidator> = {
  text: isText,
  phone: isText,
  number: isNumber,
  date: isDate,
  time: isTime,
  select: isSelect,
  file: isFile,
  checkbox: isCheckbox,
};

interface WalkableField {
  key?: unknown;
  type?: unknown;
}

const arrayAt = (node: unknown, key: string): Array<unknown> => {
  const value = (node as Record<string, unknown> | null | undefined)?.[key];

  return Array.isArray(value) ? value : [];
};

/* `steps[].sections[].fields[]`, read defensively — nothing else here walks a definition. */
const collectFields = (definition: FormDefinition | null | undefined): Array<WalkableField> =>
  arrayAt(definition, 'steps')
    .flatMap(step => arrayAt(step, 'sections'))
    .flatMap(section => arrayAt(section, 'fields')) as Array<WalkableField>;

/*
 * `hasOwnProperty`, not `key in values`: `in` walks the prototype chain, so a field keyed
 * `constructor` would match on `Object.prototype` and block submission for an unanswered field.
 */
const isAnswered = (values: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(values, key) && !isEmptyAnswer(values[key]);

/*
 * `=== false` is load-bearing: an unmodelled type yields `undefined` from the optional call, which
 * is not `false`, so the answer is left alone. `!validate(value)` would report every answer of an
 * unknown type as wrong.
 */
const isWrongKind = (type: string, key: string, values: Record<string, unknown>): boolean =>
  isAnswered(values, key) && ANSWER_VALIDATORS[type]?.(values[key]) === false;

/*
 * Returns keys, never labels: a field's `label` is rich text this app cannot flatten, so naming one
 * risks printing markup at a tenant. The renderer has already drawn an inline error on each.
 */
export const findInvalidAnswerKeys = (
  definition: FormDefinition | null | undefined,
  values: Record<string, unknown> | undefined,
): Array<string> => {
  if (!values) {
    return [];
  }

  return collectFields(definition)
    .filter(field => typeof field.key === 'string' && typeof field.type === 'string')
    .filter(field => isWrongKind(field.type as string, field.key as string, values))
    .map(field => field.key as string);
};
